/**
 * DailyCheckExecutionView.js
 *
 * Full-screen execution view for daily checks.
 * Commercial-grade with proper audit data capture.
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  X,
  Check,
  CheckCircle,
  AlertTriangle,
  User,
  Calendar,
  Clock,
  Ship,
  MessageSquare
} from 'lucide-react';
import { useVessel } from '../../../../contexts/VesselContext';
import { usePermissions } from '../../../../hooks/usePermissions';
// FT-324 cycle 3: read the eagerly-mounted single instance from context.
import { useChecklistRecordsContext } from '../../../../hooks/domain/operations/ChecklistRecordsContext';
import { useVoyages } from '../../../../hooks/domain/operations/useVoyages';
import { CHECKLIST_TYPES } from '../../../../schemas/checklistCompletionSchema';
import { portLabel } from '../../../../utils/voyage/portLabel';
import ContextLinkBanner from './ContextLinkBanner';
// FT-356: register onClose as the shell-level back handler so users can
// escape this full-screen execution view via the MobileBottomBar.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';

const DailyCheckExecutionView = ({ category, onClose, onComplete }) => {
  useSetBackHandler(onClose);
  const { getCurrentVessel } = useVessel();
  const { currentUser } = usePermissions();
  const { saveCompletionRecord } = useChecklistRecordsContext();
  const { activeVoyage } = useVoyages();
  const vessel = getCurrentVessel();

  // Auto-populated context
  const now = useMemo(() => new Date(), []);
  const dateString = now.toLocaleDateString('en-GB', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  const timeString = now.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit'
  });

  // Context linking - auto-detect if voyage is underway
  const [linkedContext, setLinkedContext] = useState(null);

  useEffect(() => {
    // Auto-link to active voyage if underway
    if (activeVoyage && activeVoyage.status === 'underway') {
      setLinkedContext({
        type: 'voyage',
        id: activeVoyage.voyageNumber || activeVoyage.id,
        name: `${portLabel(activeVoyage.from)} → ${portLabel(activeVoyage.to)}`,
        autoLinked: true
      });
    }
  }, [activeVoyage]);

  // State
  const [checkStates, setCheckStates] = useState(
    category.checks.reduce((acc, check) => {
      acc[check.id] = { passed: false, notes: '' };
      return acc;
    }, {})
  );
  const [generalNotes, setGeneralNotes] = useState('');
  const [showNotes, setShowNotes] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);  // FT-80 Phase 14: Track submit attempts

  // Progress tracking (required enforcement removed - FT-80 Phase 9)
  const completedCount = category.checks.filter(c => checkStates[c.id]?.passed).length;

  // FT-80 Phase 10: Check if all skipped items have explanations
  const skippedChecksWithoutNotes = category.checks.filter(check => {
    const state = checkStates[check.id];
    return !state?.passed && (!state?.notes || state.notes.trim() === '');
  });
  const hasSkippedWithoutExplanation = skippedChecksWithoutNotes.length > 0;
  const allChecksComplete = completedCount === category.checks.length;
  const canSubmit = allChecksComplete || !hasSkippedWithoutExplanation;

  // FT-80 Phase 14: Only show "needs explanation" highlighting AFTER user tries to submit
  const showValidation = hasAttemptedSubmit && !allChecksComplete;

  // Handlers
  const handleToggleCheck = useCallback((checkId) => {
    setCheckStates(prev => ({
      ...prev,
      [checkId]: { ...prev[checkId], passed: !prev[checkId].passed }
    }));
  }, []);

  const handleCheckNotes = useCallback((checkId, notes) => {
    setCheckStates(prev => ({
      ...prev,
      [checkId]: { ...prev[checkId], notes }
    }));
  }, []);

  const handleToggleNotes = useCallback((checkId) => {
    setShowNotes(prev => ({
      ...prev,
      [checkId]: !prev[checkId]
    }));
  }, []);

  const handleSubmit = useCallback(async () => {
    // FT-80 Phase 14: Mark that user has attempted to submit
    setHasAttemptedSubmit(true);

    // If can't submit, just return (don't submit, but now validation will show)
    if (!canSubmit) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const checks = category.checks.map(check => ({
      id: check.id,
      text: check.text,
      passed: checkStates[check.id].passed,
      notes: checkStates[check.id].notes || null
    }));

    const result = saveCompletionRecord({
      checklistType: CHECKLIST_TYPES.DAILY_CHECK,
      checklistId: category.id,
      checklistName: category.label,
      completedBy: {
        id: currentUser?.id || 'user-1',
        name: currentUser?.name || 'Crew Member',
        role: currentUser?.role || 'Crew'
      },
      checks,
      notes: generalNotes,
      linkedContext
    });

    if (result.success) {
      onComplete?.(result.record);
      onClose();
    } else {
      setError(result.errors.join(', '));
      setIsSubmitting(false);
    }
  }, [category, checkStates, generalNotes, currentUser, linkedContext, saveCompletionRecord, onComplete, onClose]);

  return (
    <div className="fixed inset-0 z-50 bg-gray-50 overflow-y-auto">
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
        <div className="px-4 py-4 sm:px-6">
          <div className="flex items-center gap-4">
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-gray-600" />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-semibold text-gray-900">{category.label}</h1>
              <p className="text-sm text-gray-500">Daily Check</p>
            </div>
          </div>
        </div>
      </div>

      {/* Context Bar - Auto-populated audit fields */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 sm:px-6">
        {/* Context Link Banner - shows if linked to voyage */}
        <ContextLinkBanner
          linkedContext={linkedContext}
          onUnlink={() => setLinkedContext(null)}
          isAutoLinked={linkedContext?.autoLinked}
        />

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-gray-400" />
            <span className="text-gray-600">{dateString}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-gray-400" />
            <span className="text-gray-600">{timeString}</span>
          </div>
          <div className="flex items-center gap-2">
            <Ship className="h-4 w-4 text-gray-400" />
            <span className="text-gray-900 font-medium">{vessel?.name || 'No Vessel'}</span>
          </div>
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-gray-400" />
            <span className="text-gray-900 font-medium">{currentUser?.name || 'Crew Member'}</span>
          </div>
        </div>
      </div>

      {/* Content - Add padding for fixed footer */}
      <div className="px-4 py-4 sm:px-6 pb-48">
        {/* Progress indicator */}
        <div className="mb-4">
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-gray-600">Progress</span>
            <span className="font-medium">
              {completedCount} / {category.checks.length}
            </span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-cyan-500 transition-all duration-300"
              style={{
                width: `${(completedCount / category.checks.length) * 100}%`
              }}
            />
          </div>
        </div>

        {/* Checks List - FT-95: Redesigned to match GenericChecklistExecutionView pattern */}
        <div className="space-y-2">
          {category.checks.map((check, index) => {
            const isSkipped = !checkStates[check.id]?.passed;
            // FT-80 Phase 14: Only show validation after submit attempt
            const needsExplanation = showValidation && isSkipped && (!checkStates[check.id]?.notes || checkStates[check.id].notes.trim() === '');
            const hasNotes = checkStates[check.id]?.notes?.trim();

            return (
            <div
              key={check.id}
              className={`bg-white rounded-xl border transition-all ${
                checkStates[check.id]?.passed
                  ? 'border-green-200 bg-green-50/50'
                  : needsExplanation
                    ? 'border-amber-300'
                    : 'border-gray-200'
              }`}
            >
              {/* Main check row - tap anywhere to toggle */}
              <button
                onClick={() => handleToggleCheck(check.id)}
                className="w-full flex items-start gap-3 p-4"
              >
                {/* FT-95: 24px ring-style checkbox (matching GenericChecklistExecutionView) */}
                <div
                  className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                    checkStates[check.id]?.passed
                      ? 'bg-green-500 border-green-500'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  {checkStates[check.id]?.passed && (
                    <Check className="h-4 w-4 text-white" />
                  )}
                </div>

                {/* Check text */}
                <div className="flex-1 text-left min-w-0">
                  <p className={`font-medium ${
                    checkStates[check.id]?.passed ? 'text-green-800' : 'text-gray-900'
                  }`}>
                    {check.text}
                  </p>
                  {check.hint && (
                    <p className="text-sm text-gray-500 mt-1">{check.hint}</p>
                  )}
                  {/* FT-80: Show validation message inline when needed */}
                  {needsExplanation && (
                    <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Reason required - why was this skipped?
                    </p>
                  )}
                </div>

                {/* FT-95: Notes icon button in corner (with e.stopPropagation) */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleNotes(check.id);
                  }}
                  className={`p-1.5 rounded-lg transition-colors flex-shrink-0 ${
                    showNotes[check.id] || hasNotes
                      ? 'bg-cyan-100 text-cyan-600'
                      : needsExplanation
                        ? 'bg-amber-100 text-amber-600'
                        : 'text-gray-400 hover:bg-gray-100'
                  }`}
                  title={hasNotes ? 'Notes added' : 'Add notes'}
                >
                  <MessageSquare className="h-4 w-4" />
                </button>
              </button>

              {/* FT-95: Notes field - only shown when icon is tapped (NOT auto-expanded) */}
              {showNotes[check.id] && (
                <div className="px-4 pb-4">
                  <textarea
                    value={checkStates[check.id]?.notes || ''}
                    onChange={(e) => handleCheckNotes(check.id, e.target.value)}
                    placeholder={needsExplanation
                      ? "Explain why this check was skipped..."
                      : "Any observations or issues..."
                    }
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 ${
                      needsExplanation
                        ? 'border-amber-300 focus:ring-amber-500'
                        : 'border-gray-200 focus:ring-cyan-500'
                    }`}
                    rows={2}
                  />
                </div>
              )}
            </div>
          );
          })}
        </div>

        {/* General Notes */}
        <div className="mt-6 bg-white rounded-xl border border-gray-200 p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            General Notes (optional)
          </label>
          <textarea
            value={generalNotes}
            onChange={(e) => setGeneralNotes(e.target.value)}
            placeholder="Any overall observations, weather conditions, or other notes..."
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
            rows={3}
          />
        </div>
      </div>

      {/* Fixed Footer */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 px-4 py-4 sm:px-6 safe-area-pb">
        {error && (
          <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {error}
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className={`w-full py-4 rounded-xl font-semibold text-lg flex items-center justify-center gap-2 transition-colors ${
            isSubmitting
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : (hasAttemptedSubmit && !canSubmit)
                ? 'bg-amber-500 text-white hover:bg-amber-600 active:bg-amber-700'
                : 'bg-green-600 text-white hover:bg-green-700 active:bg-green-800'
          }`}
        >
          <CheckCircle className="h-6 w-6" />
          {isSubmitting
            ? 'Saving...'
            : (hasAttemptedSubmit && !canSubmit)
              ? `${skippedChecksWithoutNotes.length} skipped check(s) need explanation`
              : `Complete ${category.label}`
          }
        </button>

        <p className="text-center text-xs text-gray-500 mt-2">
          {(hasAttemptedSubmit && !canSubmit)
            ? 'Provide a reason for each skipped check before submitting'
            : 'Record will be locked after submission for audit compliance'
          }
        </p>
      </div>
    </div>
  );
};

export default DailyCheckExecutionView;
